// src/components/TiptapEditor.js
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus"; // <-- NOVO CAMINHO
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import Strike from "@tiptap/extension-strike";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import BaseImage from "@tiptap/extension-image";
import { Mark, Node, mergeAttributes } from "@tiptap/core";
import axios from "../services/axiosInstance";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { asBlob } from "html-docx-js-typescript";
import ModalOCR from "./Modals/ModalOCR";
// IMPORTANDO SEU CONFIRM DIALOG CUSTOMIZADO
import ConfirmDialog from "./Modals/ConfirmDialog";

// Ícones Lucide
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3, Pilcrow,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo2, Redo2, Maximize2, Minimize2, 
  FileUp, Table as TableIcon, Image as ImageIcon, Eraser, Code2, MoreVertical, 
  CaseUpper, CaseLower, ChevronDown, BookOpen, Printer, FileDown, PenTool, 
  Strikethrough, UserCheck, Users, Trash2, FileText, Stamp, Grid, Eye, EyeOff, 
  Plus, Minus, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, SquareX, ClipboardPaste,
  LayoutTemplate
} from "lucide-react";

import styles from "./Modals/Modal.module.css";

/* =======================
   CSS INTERNO (Sem font-size em tags gerais)
======================= */
const internalCSS = `
.tiptap { 
    outline: none !important;
    width: 100%;
    background-color: #fff;
    padding: 1.6rem; /* 16px */
    box-sizing: border-box;
}
.tiptap p { margin-bottom: 0.8rem; line-height: 1.5; } 
.tiptap strong, .tiptap b { font-weight: 700 !important; }
.tiptap em, .tiptap i { font-style: italic !important; }
.tiptap u { text-decoration: underline !important; }

/* ESTILOS LEGISLATIVOS */
.tiptap p.artigo { font-weight: 600; margin: 0.8rem 0; }
.tiptap p.inciso { margin-left: 2.4rem; }
.tiptap p.alinea { margin-left: 4.0rem; }

/* TABELAS */
.tiptap table { border-collapse: collapse; width: 100%; margin: 1.6rem 0; table-layout: fixed; }
.tiptap table th, .tiptap table td { border: 0.1rem solid #000; padding: 0.8rem; vertical-align: top; }
.tiptap table.no-border, .tiptap table.no-border th, .tiptap table.no-border td { border: 0.1rem dashed #ccc !important; }

@media print {
    .tiptap table.no-border, .tiptap table.no-border th, .tiptap table.no-border td { border: none !important; }
    .bubble-menu, .toolbar, .no-print { display: none !important; }
    .page-break { page-break-after: always; }
}
`;

/* =======================
   EXTENSÕES TIPTAP
======================= */
const FontFamily = Mark.create({ name: "fontFamily", addAttributes() { return { 'data-font-family': { default: null } } }, parseHTML: () => [{ style: 'font-family' }], renderHTML({ HTMLAttributes }) { return ['span', mergeAttributes(HTMLAttributes, { style: HTMLAttributes['data-font-family'] ? `font-family:${HTMLAttributes['data-font-family']}` : undefined }), 0] } });
const TextTransform = Mark.create({ name: "textTransform", addAttributes() { return { transform: { default: null, parseHTML: el => el.style.textTransform, renderHTML: attrs => attrs.transform ? { style: `text-transform: ${attrs.transform}` } : {} } } }, addCommands() { return { toggleUppercase: () => ({ chain, editor }) => editor.isActive(this.name, {transform: 'uppercase'}) ? chain().unsetMark(this.name).run() : chain().setMark(this.name, {transform: 'uppercase'}).run(), toggleLowercase: () => ({ chain, editor }) => editor.isActive(this.name, {transform: 'lowercase'}) ? chain().unsetMark(this.name).run() : chain().setMark(this.name, {transform: 'lowercase'}).run() } } });
const LineHeight = Node.create({ name: "lineHeight", addGlobalAttributes() { return [{ types: ["paragraph", "heading"], attributes: { lineHeight: { default: null, parseHTML: el => el.style.lineHeight, renderHTML: attrs => attrs.lineHeight ? { style: `line-height:${attrs.lineHeight}` } : {} }, class: { default: null, parseHTML: el => el.getAttribute("class"), renderHTML: attrs => attrs.class ? { class: attrs.class } : {} } } }] } });
const PageBreak = Node.create({
  name: "pageBreak",
  group: "block",
  atom: true,
  parseHTML: () => [{ tag: 'hr[data-page-break="true"]' }],
  renderHTML: () => ['hr', { 'data-page-break': 'true', class: 'page-break' }],
  addCommands() {
    return { insertPageBreak: () => ({ chain }) => chain().insertContent({ type: this.name }).run() }
  }
});
const ResizableImage = BaseImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: '100%', parseHTML: el => el.style.width, renderHTML: attrs => ({ style: `width: ${attrs.width}; height: auto;` }) },
      align: { default: 'center', parseHTML: el => el.getAttribute('data-align'), renderHTML: attrs => { const style = `width: ${attrs.width}; height: auto;`; if (attrs.align === 'left') return { 'data-align': 'left', style: `${style} float: left; margin: 0.5rem 1rem 0.5rem 0;` }; if (attrs.align === 'right') return { 'data-align': 'right', style: `${style} float: right; margin: 0.5rem 0 0.5rem 1rem;` }; return { 'data-align': 'center', style: `${style} display: block; margin: 0.5rem auto;` }; } },
    };
  }
});

const CustomTable = Table.extend({
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attrs => {
            if (attrs.class) { return { class: attrs.class }; }
            return {};
        }
      }
    }
  }
});

/* =======================
   TOOLBAR COMPONENTS
======================= */
const ToolbarButton = ({ onClick, isActive, disabled, title, children, hasArrow, style }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} className={`${styles.btnPreto} ${isActive ? styles.ativo : ""}`} disabled={disabled} title={title} style={{ display: "flex", alignItems: "center", gap: 3, ...style }}>
    {children} {hasArrow && <ChevronDown size={10} style={{ opacity: 0.6 }} />}
  </button>
);

const ToolbarDropdown = ({ icon, label, title, children, isActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={ref}>
      <ToolbarButton onClick={() => setIsOpen(!isOpen)} title={title} isActive={isActive || isOpen} hasArrow>{icon} {label && <span style={{fontWeight: 500}}>{label}</span>}</ToolbarButton>
      {isOpen && <div style={{ position: 'absolute', top: '100%', left: 0, backgroundColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, border: '1px solid #ddd', borderRadius: '4px', padding: '4px', display: 'flex', flexDirection: 'column', maxHeight: '350px', overflowY: 'auto' }} onClick={() => setIsOpen(false)}>{children}</div>}
    </div>
  );
};
const DropdownItem = ({ onClick, children, title }) => <button type="button" onClick={onClick} title={title} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 10px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: "#333" }} onMouseEnter={(e)=>e.currentTarget.style.background='#f3f4f6'} onMouseLeave={(e)=>e.currentTarget.style.background='transparent'}>{children}</button>;

/* =======================
   COMPONENTE PRINCIPAL
======================= */
export default function TiptapEditor({ 
    value, 
    onChange, 
    placeholder = "Digite aqui...", 
    showDocxImport = true,
    materiaId = null,
    autores = [],
    onFileImported
}) {
  const [fullscreen, setFullscreen] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlBuffer, setHtmlBuffer] = useState("");
  const [blockMenu, setBlockMenu] = useState({ visible: false, x: 0, y: 0, kind: null });
  
  const [modelosDisponiveis, setModelosDisponiveis] = useState([]);
  const [camaraInfo, setCamaraInfo] = useState(null);

  // ESTADO PARA O CONFIRM DIALOG DE MODELOS
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", onConfirm: null });

  const fileInputRef = useRef(null);
  const imgFileInputRef = useRef(null);
  const editorContainerRef = useRef(null);
  const [mostrarOCR, setMostrarOCR] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
        try {
            const [modelosRes, camaraRes] = await Promise.all([
                axios.get("/documentos/modelos/listar", { params: { ativosOnly: true } }).catch(() => ({ data: [] })),
                axios.get("/camara-municipal").catch(() => ({ data: null }))
            ]);
            setModelosDisponiveis(modelosRes.data || []);
            setCamaraInfo(camaraRes.data);
        } catch (error) {
            console.error("Erro ao carregar dados do editor", error);
        }
    };
    fetchData();
  }, []);

  const uploadImage = async (file) => {
    const form = new FormData(); form.append("file", file);
    try { 
      const { data } = await axios.post("/documentos/upload", form, { headers: { "Content-Type": "multipart/form-data" } }); 
      return data?.url; 
    } catch { 
      toast.error("Erro no upload da imagem");
      return null; 
    }
  };

  const editor = useEditor({
    autofocus: false,
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: true, autolink: true, HTMLAttributes: { target: "_blank", rel: "noopener" } }),
      Highlight, Subscript, Superscript, Strike, 
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle, Color,
      Placeholder.configure({ placeholder }),
      CharacterCount.configure(),
      ResizableImage.configure({ inline: false, allowBase64: true }),
      FontFamily, TextTransform, LineHeight, PageBreak,
      CustomTable.configure({ resizable: true }), TableRow, TableHeader, TableCell,
    ],
    content: value ?? "",
    editorProps: { 
        attributes: { class: "tiptap" },
        handlePaste: (view, event) => {
            const items = event.clipboardData?.items || [];
            for (const it of items) {
              if (it.kind === "file" && /^image\//.test(it.type)) {
                const file = it.getAsFile();
                uploadImage(file).then(url => { if (url) editor.commands.setImage({ src: url }); });
                return true;
              }
            }
            return false;
          },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  useEffect(() => { if (editor && typeof value === "string" && value !== editor.getHTML()) editor.commands.setContent(value, false); }, [value, editor]);

  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isDocx = file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" || file.name.endsWith('.docx');

    if (!isDocx) {
        toast.warning("Formato não suportado. Por favor, importe um arquivo .docx");
        return;
    }

    try {
        const { default: mammoth } = await import("mammoth");
        const arrayBuffer = await file.arrayBuffer();
        
        const result = await mammoth.convertToHtml({ arrayBuffer });
        const html = result.value;

        if (editor) { 
            editor.commands.setContent(html, true); 
            onChange?.(html); 
        }

        if (onFileImported) onFileImported(file, html);
        
        toast.success("Documento importado com sucesso!");
    } catch (error) {
        console.error("Erro na importação:", error);
        toast.error("Falha ao ler o arquivo DOCX.");
    } finally {
        event.target.value = null; 
    }
  };

  const exportToDocx = async () => {
    if (!editor) return;
    const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>${internalCSS}</style></head><body>${editor.getHTML()}</body></html>`;
    const buffer = await asBlob(htmlContent, { orientation: "portrait", margins: { top: 720, right: 720, bottom: 720, left: 720 } });
    saveAs(buffer, "documento.docx");
  };

  const handlePrint = async (comTimbre = false) => {
    if (!editor) return;
    
    const content = editor.getHTML();
    const printWindow = window.open('', '_blank', 'width=850,height=900');
    if (!printWindow) return alert("Por favor, permita popups para imprimir.");

    const listaAutores = Array.isArray(autores) ? autores : (autores ? [autores] : []);
    
    let assinaturasHtml = '';
    if (listaAutores.length > 0) {
        assinaturasHtml = `
            <div style="display: flex; flex-wrap: wrap; justify-content: center; margin-top: 6.0rem; gap: 4.0rem; page-break-inside: avoid;">
                ${listaAutores.map(autor => {
                    const agora = new Date();
                    const dataFormatada = agora.toISOString().split('T')[0].replace(/-/g, '.');
                    const horaFormatada = agora.toTimeString().split(' ')[0];
                    const carimboData = `${dataFormatada} ${horaFormatada} -03'00'`;
                    
                    const nomeExibicao = autor.nome ? autor.nome.toUpperCase() : 'NOME NÃO IDENTIFICADO';
                    const cargoExibicao = autor.cargo || 'Vereador';

                    return `
                        <div style="width: 32.0rem; font-family: sans-serif; text-align: left;">
                            <div style="display: flex; align-items: center; border-bottom: 0.1rem solid #000; padding-bottom: 0.5rem; margin-bottom: 0.8rem; position: relative;">
                                <div style="color: #d32f2f; font-size: 1.1rem; line-height: 1.2; z-index: 2;">
                                    <strong>${nomeExibicao}</strong><br>
                                    <span style="font-size: 0.9rem;">Assinado de forma digital por</span><br>
                                    <span style="font-size: 1.0rem;">${nomeExibicao}</span><br>
                                    <span style="font-size: 0.9rem;">Dados: ${carimboData}</span>
                                </div>
                                ${autor.assinatura_imagem ? `
                                    <img src="${axios.defaults.baseURL}/images/usuarios/${autor.assinatura_imagem}" 
                                         style="height: 5.0rem; position: absolute; right: 0; opacity: 0.6; z-index: 1;" />
                                ` : ''}
                            </div>
                            <div style="text-align: center;">
                                <div style="font-weight: bold; font-size: 1.4rem;">${nomeExibicao}</div>
                                <div style="font-size: 1.3rem; color: #333;">${cargoExibicao}</div>
                            </div>
                        </div>
                    `;
                }).join('')}
            </div>
        `;
    }

    const logoUrl = camaraInfo?.logo ? `${axios.defaults.baseURL}/uploads/logos/${camaraInfo.logo}` : "";
    const header = comTimbre ? `
        <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 0.2rem solid #000; margin-bottom: 3.0rem; padding-bottom: 1.0rem; font-family: 'Times New Roman', serif;">
            <img src="${logoUrl}" style="height: 8.0rem; width: auto; object-fit: contain;" />
            <div style="text-align: right;">
                <h1 style="margin: 0; font-size: 2.2rem; text-transform: uppercase;">${camaraInfo?.nome || ''}</h1>
                <p style="margin: 0.4rem 0; font-size: 1.4rem;">${camaraInfo?.endereco || ''}</p>
                <p style="margin: 0; font-size: 1.4rem;">${camaraInfo?.cidade || ''} - ${camaraInfo?.estado || ''} | CEP: ${camaraInfo?.cep || ''}</p>
            </div>
        </div>
    ` : '';

    printWindow.document.write(`
        <html>
            <head>
                <title>Plenário Digital - Impressão</title>
                <style>
                    @page { size: A4; margin: 2.5cm; }
                    body { font-family: 'Times New Roman', serif; margin: 0; padding: 0; }
                    ${internalCSS}
                </style>
            </head>
            <body>
                ${header}
                <div class="tiptap">
                    ${content}
                    ${assinaturasHtml}
                </div>
                <script>
                    window.onload = () => { 
                        window.print(); 
                        setTimeout(() => window.close(), 500); 
                    };
                </script>
            </body>
        </html>
    `);
    printWindow.document.close();
  };

  const inserirArtigo = () => {
    const todosArtigos = editor.getHTML().match(/Art\.\s*(\d+)/g) || [];
    const proximoNumero = todosArtigos.length + 1;
    editor.chain().focus().insertContent(`<p class="artigo">Art. ${proximoNumero}º - </p>`).run();
  };

  const inserirInciso = () => {
    editor.chain().focus().insertContent(`<p class="inciso">I - </p>`).run();
  };

  const inserirAlinea = () => {
    editor.chain().focus().insertContent(`<p class="alinea">a) - </p>`).run();
  };

  // --- MODELOS REFATORADO (Sem Window.Confirm) ---
  const aplicarModelo = (htmlModelo) => {
    if (!editor) return;

    // Removemos tags html limpas para validar se de fato há algum texto valioso escrito.
    const htmlAtual = editor.getHTML();
    const textoLimpo = htmlAtual.replace(/<[^>]*>?/gm, '').trim();
    const isVazio = editor.isEmpty || textoLimpo === '';

    if (!isVazio) {
        setConfirmDialog({
            open: true,
            title: "Aplicar Modelo",
            message: "Isso substituirá todo o conteúdo atual da redação. Deseja continuar?",
            onConfirm: () => {
                editor.commands.setContent(htmlModelo);
                toast.success("Modelo aplicado!");
                setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
            }
        });
        return;
    }

    editor.commands.setContent(htmlModelo);
    toast.success("Modelo aplicado!");
  };

  const toggleTableBorder = () => {
    if (!editor) return;
    const currentClass = editor.getAttributes('table').class;
    const hasNoBorder = currentClass && currentClass.includes('no-border');
    editor.chain().focus().updateAttributes('table', { class: hasNoBorder ? null : 'no-border' }).run();
  };
  
  const insertTable = () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  const isTableBorderHidden = editor && editor.getAttributes('table').class?.includes('no-border');

  const BubbleBtn = ({ onClick, children, className, title, style }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} className={className} title={title} style={style}>{children}</button>
  );

  const handlePasteHtml = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) { editor.chain().focus().insertContent(text).run(); toast.success("HTML colado!"); }
    } catch { toast.error("Erro ao ler clipboard."); }
  };

  if (!editor) return null;

  return (
    <div 
        className={fullscreen ? styles.modal : ""}
        style={{ borderRadius: fullscreen ? 0 : '3px', background: '#f9f9f9', display:'flex', flexDirection:'column', width: fullscreen ? "100vw" : "100%", height: fullscreen ? "100vh" : "auto", boxSizing: "border-box", position: fullscreen ? 'fixed' : 'relative', top: fullscreen ? 0 : 'auto', left: fullscreen ? 0 : 'auto', zIndex: fullscreen ? 9999 : 'auto', padding: fullscreen ? 0 : 0 }}
    >
       <style>{internalCSS}</style>

       {/* --- TOOLBAR --- */}
       <div className={styles.toolbar} onMouseDown={(e) => e.preventDefault()}>
        {showDocxImport && <><ToolbarButton className={styles.btnPrincipal} onClick={() => fileInputRef.current?.click()} title="Importar Arquivo"><FileUp size={15} /> Importar</ToolbarButton>
        <ToolbarButton onClick={() => setMostrarOCR(true)} title="Extrair Texto de PDF (OCR)">
    <FileText size={15} /> OCR
</ToolbarButton>
        <input className={styles.uploadInputHidden} type="file" ref={fileInputRef} onChange={handleImportFile} accept=".docx" hidden /></>}
          <div style={{display:'flex', gap:2}}>
             <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo2 size={15} /></ToolbarButton>
             <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo2 size={15} /></ToolbarButton>
          </div>
          <div style={{width:1, background:'#ccc', margin:'0 4px'}} />

          <ToolbarDropdown icon={<Bold size={15} />} title="Formatação" isActive={editor.isActive('bold')}>
             <DropdownItem onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={14}/> Negrito</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14}/> Itálico</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={14}/> Sublinhado</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={14}/> Riscar</DropdownItem>
             <div style={{height:1, background:'#eee', margin:'2px 0'}}/>
             <DropdownItem onClick={() => editor.chain().focus().toggleUppercase().run()}><CaseUpper size={14}/> MAIÚSCULAS</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleLowercase().run()}><CaseLower size={14}/> minúsculas</DropdownItem>
          </ToolbarDropdown>

          <ToolbarDropdown icon={<Pilcrow size={15} />} label={editor.isActive('heading') ? `H${editor.getAttributes('heading').level}` : "P"} title="Estilo">
             <DropdownItem onClick={() => editor.chain().focus().setParagraph().run()}>Texto Normal</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>Título 1</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>Título 2</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>Título 3</DropdownItem>
          </ToolbarDropdown>

          <ToolbarDropdown icon={<AlignLeft size={15} />} title="Alinhamento">
             <DropdownItem onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft size={14}/> Esquerda</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter size={14}/> Centro</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight size={14}/> Direita</DropdownItem>
             <DropdownItem onClick={() => editor.chain().focus().setTextAlign("justify").run()}><AlignJustify size={14}/> Justificado</DropdownItem>
          </ToolbarDropdown>

          <ToolbarDropdown icon={<BookOpen size={15}/>} title="Estrutura Legislativa" label="Legis">
             <DropdownItem onClick={inserirArtigo} title="Inserir Artigo"><span style={{fontWeight:'bold', minWidth:20}}>Art.</span> Artigo</DropdownItem>
             <DropdownItem onClick={inserirInciso} title="Inserir Inciso"><span style={{fontWeight:'bold', minWidth:20}}>I</span> Inciso</DropdownItem>
             <DropdownItem onClick={inserirAlinea} title="Inserir Alínea"><span style={{fontWeight:'bold', minWidth:20}}>a</span> Alínea</DropdownItem>
          </ToolbarDropdown>

          <ToolbarDropdown icon={<LayoutTemplate size={15} />} title="Modelos" label="Modelos">
             {modelosDisponiveis.length > 0 ? modelosDisponiveis.map(m => (
                <DropdownItem key={m.id} onClick={() => aplicarModelo(m.conteudoHtml)}>
                    <FileText size={14} style={{marginRight:5, opacity:0.6}}/> {m.nome || "Modelo sem título"}
                </DropdownItem>
             )) : <div style={{padding:'8px 12px', color:'#999'}}>Nenhum modelo.</div>}
          </ToolbarDropdown>

          <ToolbarDropdown icon={<TableIcon size={15} />} title="Tabelas">
             <DropdownItem onClick={insertTable}>Nova Tabela 3x3</DropdownItem>
          </ToolbarDropdown>

             <ToolbarButton onClick={exportToDocx} title="Baixar DOCX"><FileDown size={15}/></ToolbarButton>
             <ToolbarDropdown icon={<Printer size={15} />} title="Opções de Impressão">
                <DropdownItem onClick={() => handlePrint(false)}><FileText size={14} style={{marginRight:5}}/> Simples</DropdownItem>
                <DropdownItem onClick={() => handlePrint(true)}><Stamp size={14} style={{marginRight:5}}/> Com Cabeçalho</DropdownItem>
             </ToolbarDropdown>
             <div style={{width:1, background:'#ccc', margin:'0 4px'}} />
             <ToolbarButton onClick={handlePasteHtml} title="Colar HTML Renderizado"><ClipboardPaste size={15} /> Colar HTML</ToolbarButton>
             <ToolbarButton onClick={() => imgFileInputRef.current?.click()} title="Inserir Imagem"><ImageIcon size={15} /></ToolbarButton>
             <input className={styles.uploadInputHidden} ref={imgFileInputRef} type="file" accept="image/*" hidden onChange={(e) => {const f=e.target.files[0]; if(f) uploadImage(f).then(u=>u && editor.commands.setImage({src:u}));}} />
             <ToolbarButton onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Limpar"><Eraser size={15} /></ToolbarButton>
             <ToolbarButton onClick={() => setHtmlMode(true)} title="HTML"><Code2 size={15} /> Editar HTML</ToolbarButton>
             
             <div style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
                 <ToolbarButton 
                     onClick={() => setFullscreen(!fullscreen)} 
                     title={fullscreen ? "Sair da Tela Cheia" : "Tela Cheia"}
                 >
                     {fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                 </ToolbarButton>

                 {fullscreen && (
                     <ToolbarButton 
                         onClick={() => setFullscreen(false)} 
                         title="Fechar"
                     >
                         <SquareX size={15} color="#d32f2f" />
                     </ToolbarButton>
                 )}
             </div>
       </div>

       {/* ÁREA DO EDITOR */}
       <div ref={editorContainerRef} className="tiptap-host" style={{ height: fullscreen ? 'calc(100vh - 50px)' : '300px', overflowY: 'auto', backgroundColor: fullscreen ? '#e5e7eb' : '#fff', display: 'flex', justifyContent: 'center', padding: '20px' }}>
         
         {/* BUBBLE MENU IMAGEM */}
         {editor && (
           <BubbleMenu editor={editor} options={{ placement: 'top' }} shouldShow={({ editor }) => editor.isActive('image')}>
             <div className="bubble-menu">
               <div className="bubble-row">
                   <div className="btn-group">
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { align: 'left' }).run()} className={editor.isActive('image', { align: 'left' }) ? 'is-active' : ''} title="Esquerda"><AlignLeft size={16}/></BubbleBtn>
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { align: 'center' }).run()} className={editor.isActive('image', { align: 'center' }) ? 'is-active' : ''} title="Centro"><AlignCenter size={16}/></BubbleBtn>
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { align: 'right' }).run()} className={editor.isActive('image', { align: 'right' }) ? 'is-active' : ''} title="Direita"><AlignRight size={16}/></BubbleBtn>
                   </div>
                   <div className="btn-group">
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { width: '25%' }).run()} style={{ fontWeight:600}}>25%</BubbleBtn>
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { width: '50%' }).run()} style={{ fontWeight:600}}>50%</BubbleBtn>
                       <BubbleBtn onClick={() => editor.chain().focus().updateAttributes('image', { width: '100%' }).run()} style={{fontWeight:600}}>100%</BubbleBtn>
                   </div>
                   <BubbleBtn onClick={() => editor.chain().focus().deleteSelection().run()} className="danger" title="Remover"><Trash2 size={16}/></BubbleBtn>
               </div>
             </div>
           </BubbleMenu>
         )}

         {/* BUBBLE MENU TABELA */}
         {editor && (
           <BubbleMenu editor={editor} tippyOptions={{ duration: 100, maxWidth: 600 }} shouldShow={({ editor }) => editor.isActive('table')}>
              <div className="bubble-menu">
                 <div className="bubble-row" style={{justifyContent: 'space-between'}}>
                     <BubbleBtn onClick={toggleTableBorder} className={isTableBorderHidden ? 'is-active' : ''} style={{width:'auto', padding:'0 8px', gap:5}} title="Alternar Borda">
                         {isTableBorderHidden ? <><EyeOff size={14}/> Sem Borda (Oculta na Impressão)</> : <><Eye size={14}/> Com Borda (Sólida)</>}
                     </BubbleBtn>
                     <BubbleBtn onClick={() => editor.chain().focus().deleteTable().run()} className="danger" title="Excluir Tabela"><Trash2 size={14}/></BubbleBtn>
                 </div>
                 <div style={{height:1, background:'#eee'}} />

                 <div>
                   <div className="bubble-header">Colunas</div>
                   <div className="bubble-row">
                       <div className="btn-group">
                            <BubbleBtn onClick={() => editor.chain().focus().addColumnBefore().run()} title="Inserir à Esquerda"><ArrowLeft size={14}/></BubbleBtn>
                            <BubbleBtn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Inserir à Direita"><ArrowRight size={14}/></BubbleBtn>
                       </div>
                       <div className="btn-group">
                           <BubbleBtn onClick={() => editor.chain().focus().mergeCells().run()} title="Mesclar"><Grid size={14}/></BubbleBtn>
                           <BubbleBtn onClick={() => editor.chain().focus().splitCell().run()} title="Separar"><Grid size={14} style={{opacity:0.6}}/></BubbleBtn>
                       </div>
                       <span style={{flex:1}}/>
                       <BubbleBtn onClick={() => editor.chain().focus().deleteColumn().run()} className="danger" title="Excluir Coluna"><Minus size={14}/></BubbleBtn>
                   </div>
                 </div>

                 <div>
                   <div className="bubble-header">Linhas</div>
                   <div className="bubble-row">
                       <div className="btn-group">
                            <BubbleBtn onClick={() => editor.chain().focus().addRowBefore().run()} title="Inserir Acima"><ArrowUp size={14}/></BubbleBtn>
                            <BubbleBtn onClick={() => editor.chain().focus().addRowAfter().run()} title="Inserir Abaixo"><ArrowDown size={14}/></BubbleBtn>
                       </div>
                       <span style={{flex:1}}/>
                       <BubbleBtn onClick={() => editor.chain().focus().deleteRow().run()} className="danger" title="Excluir Linha"><Minus size={14}/></BubbleBtn>
                   </div>
                 </div>
              </div>
           </BubbleMenu>
         )}

         <EditorContent editor={editor} className="tiptap" style={{ height: fullscreen ? "auto" : "auto", maxHeight: fullscreen ? "800px" : "300px", width: fullscreen ? "210mm" : "100%", maxWidth: "100%", backgroundColor: "#fff", padding: fullscreen ? "9mm" : "10px", boxShadow: fullscreen ? "0 0 10px rgba(0,0,0,0.1)" : "none", outline: "none" }} />
         
         {blockMenu.visible && (
           <div className="block-kebab" style={{ left: blockMenu.x, top: blockMenu.y }} onMouseDown={(e) => e.preventDefault()}>
               <button type="button" className="kebab-btn"><MoreVertical size={14} /></button>
           </div>
         )}
       </div>

       {htmlMode && ReactDOM.createPortal(
         <div className={styles.modalOverlay} style={{zIndex: 10000}}>
             <div className={styles.modal} style={{width:'800px', background:'white', padding:20, borderRadius:5}}>
                 <div className={styles.modalTitle}><h2>Editar Código HTML</h2></div>
                 <textarea value={htmlBuffer || editor.getHTML()} onChange={e => setHtmlBuffer(e.target.value)} className={styles.textArea} style={{width:'100%', height:'400px', fontFamily:'monospace'}} />
                 <div className={styles.botoesAcaoBlocos}>
                     <button className={styles.encerrarVotacao} onClick={() => setHtmlMode(false)}>Cancelar</button>
                     <button className={styles.iniciarVotacao} onClick={() => { editor.commands.setContent(htmlBuffer); setHtmlMode(false); }}>Aplicar</button>
                 </div>
             </div>
         </div>, document.body
       )}
{mostrarOCR && <ModalOCR onClose={() => setMostrarOCR(false)} />}
       {/* RENDERIZANDO O CONFIRM DIALOG PARA O TIPTAP */}
       <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            onConfirm={confirmDialog.onConfirm}
            onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
            isDestructive={true}
       />
    </div>
  );
}